import { isAllowedFile } from "../assests/extention/fileextention";

/**
 * Validates file signature (magic bytes).
 * @param {File} file - The file to validate.
 * @param {Array<string>} allowedTypes - Array of allowed types (e.g., ['image/jpeg', 'image/png', 'application/pdf']).
 * @returns {Promise<boolean>} - True if the file signature matches its extension and is in allowedTypes.
 */
export const validateFileSignature = (file, allowedTypes) => {
    return new Promise((resolve) => {
        if (!file) {
            resolve(false);
            return;
        }

        // 🛡️ Explicitly enforce extension whitelist (blocks .jfif etc.)
        if (!isAllowedFile(file)) {
            resolve(false);
            return;
        }

        // Check if the stated MIME type is allowed
        if (!allowedTypes.includes(file.type)) {
            resolve(false);
            return;
        }

        const reader = new FileReader();
        reader.onloadend = (e) => {
            const arr = new Uint8Array(e.target.result).subarray(0, 4);
            let header = "";
            for (let i = 0; i < arr.length; i++) {
                header += arr[i].toString(16);
            }

            let type = "";
            switch (header.toLowerCase()) {
                case "89504e47":
                    type = "image/png";
                    break;
                case "47494638":
                    type = "image/gif";
                    break;
                case "25504446":
                    type = "application/pdf";
                    break;
                case "ffd8ffe0":
                case "ffd8ffe1":
                case "ffd8ffe2":
                case "ffd8ffe3":
                case "ffd8ffe8":
                    type = "image/jpeg";
                    break;
                case "504b0304":
                    // Zip / Docx / Xlsx / Pptx / Apk (All use the same header)
                    // We can't distinguish between them easily without a full zip parser
                    // but we can at least confirm it's a zip-based file.
                    type = "application/zip";
                    if (file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") type = file.type;
                    else if (file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") type = file.type;
                    else if (file.type === "application/vnd.openxmlformats-officedocument.presentationml.presentation") type = file.type;
                    else if (file.type === "application/zip") type = file.type;
                    break;
                case "d0cf11e0":
                    // Older Office docs (Doc, Xls, Ppt)
                    if (file.type === "application/msword") type = file.type;
                    else if (file.type === "application/vnd.ms-excel") type = file.type;
                    else if (file.type === "application/vnd.ms-powerpoint") type = file.type;
                    break;
                default:
                    type = "unknown";
                    break;
            }

            // Check if the detected type matches the stated file.type
            // or if it's one of the common zip/office types we can't easily distinguish.
            const isAllowed = allowedTypes.includes(type) || (type === "application/zip" && allowedTypes.some(t => t.includes("officedocument") || t === "application/zip"));

            resolve(isAllowed);
        };
        reader.readAsArrayBuffer(file.slice(0, 4));
    });
};
